export const CANAL = {
  MULTIMARCA: "MULTIMARCA",
  LOJA_PROPRIA: "LOJA_PROPRIA",
  FRANQUIA: "FRANQUIA",
};

export const TIPO_PESSOA = {
  REPRESENTANTE: "REPRESENTANTE",
  LOJISTA: "LOJISTA",
  FRANQUIA: "FRANQUIA",
  SUPERVISOR: "SUPERVISOR",
  VENDEDOR: "VENDEDOR",
};

export function getProfileName(profile) {
  return profile?.NomePerfil || profile?.Nome || "";
}

export function profileHasTipoPessoa(profile, tipo) {
  return (profile?.TiposPessoa || []).includes(tipo);
}

export function profileHasCanal(profile, canal) {
  return (profile?.Canais || []).includes(canal);
}

export function userHasTipoPessoa(profiles = [], tipo) {
  return profiles.some((profile) => profileHasTipoPessoa(profile, tipo));
}

export function userIsExclusivelyTipoPessoa(profiles = [], tipo) {
  const tiposPessoa = new Set(
    profiles.flatMap((profile) => profile?.TiposPessoa || []),
  );
  return tiposPessoa.size === 1 && tiposPessoa.has(tipo);
}

export function userHasCanal(profiles = [], canal) {
  return profiles.some((profile) => profileHasCanal(profile, canal));
}

export function userHasProfileName(profiles = [], nomePerfil) {
  return profiles.some(
    (profile) =>
      getProfileName(profile).toUpperCase() === nomePerfil.toUpperCase(),
  );
}

export function userHasAnyProfileName(profiles = [], nomes = []) {
  return nomes.some((nome) => userHasProfileName(profiles, nome));
}

/**
 * Representante "de carteira": tem a flag de representante, mas NÃO é supervisor. Supervisor tem
 * nível de acesso maior e não deve cair nas mesmas restrições de um representante comum, mesmo
 * quando o perfil também carrega p_representante=true.
 */
export function userIsRepresentante(profiles = []) {
  return (
    userHasTipoPessoa(profiles, TIPO_PESSOA.REPRESENTANTE) &&
    !userHasTipoPessoa(profiles, TIPO_PESSOA.SUPERVISOR)
  );
}

export function userIsLojista(profiles = []) {
  return userHasTipoPessoa(profiles, TIPO_PESSOA.LOJISTA);
}

export function userIsLojistaMultimarca(profiles = []) {
  return profiles.some(
    (profile) =>
      profileHasCanal(profile, CANAL.MULTIMARCA) &&
      profileHasTipoPessoa(profile, TIPO_PESSOA.LOJISTA),
  );
}

export function userIsLojistaLojaPropria(profiles = []) {
  return profiles.some(
    (profile) =>
      profileHasCanal(profile, CANAL.LOJA_PROPRIA) &&
      profileHasTipoPessoa(profile, TIPO_PESSOA.LOJISTA),
  );
}

export function userIsFranquia(profiles = []) {
  return (
    userHasCanal(profiles, CANAL.FRANQUIA) ||
    userHasTipoPessoa(profiles, TIPO_PESSOA.FRANQUIA)
  );
}

export function userIsSupervisor(profiles = []) {
  return userHasTipoPessoa(profiles, TIPO_PESSOA.SUPERVISOR);
}

export function userIsVendedor(profiles = []) {
  return userHasTipoPessoa(profiles, TIPO_PESSOA.VENDEDOR);
}

export function getSessionUserDetail() {
  try {
    const vision = JSON.parse(
      localStorage.getItem("userDetailVision") || "null",
    );
    if (vision && typeof vision === "object" && !Array.isArray(vision)) {
      return vision;
    }

    return JSON.parse(localStorage.getItem("userDetail") || "null");
  } catch {
    return null;
  }
}

export function sessionUserIsBackoffice() {
  return getSessionUserDetail()?.Backoffice === true;
}

export function getSessionUserProfiles() {
  try {
    const vision = JSON.parse(
      localStorage.getItem("userProfilesVision") || "null",
    );
    const detail = JSON.parse(localStorage.getItem("userDetail") || "null");
    const normal = JSON.parse(localStorage.getItem("userProfiles") || "[]");

    if (Array.isArray(vision) && vision.length > 0) {
      return vision;
    }

    if (
      Array.isArray(detail?.UsuarioPerfis) &&
      detail.UsuarioPerfis.length > 0
    ) {
      return detail.UsuarioPerfis;
    }

    return Array.isArray(normal) ? normal : [];
  } catch {
    return [];
  }
}

export function sessionUserHasTipoPessoa(tipo) {
  return userHasTipoPessoa(getSessionUserProfiles(), tipo);
}

export function sessionUserHasCanal(canal) {
  return userHasCanal(getSessionUserProfiles(), canal);
}

export function sessionUserIsRepresentante() {
  return userIsRepresentante(getSessionUserProfiles());
}

export function sessionUserIsLojistaMultimarca() {
  return userIsLojistaMultimarca(getSessionUserProfiles());
}

export function sessionUserIsFranquia() {
  return userIsFranquia(getSessionUserProfiles());
}

export function profileMatchesExternalAccess(profile) {
  return (
    profileHasTipoPessoa(profile, TIPO_PESSOA.REPRESENTANTE) ||
    profileHasTipoPessoa(profile, TIPO_PESSOA.LOJISTA) ||
    (profileHasCanal(profile, CANAL.MULTIMARCA) &&
      profileHasTipoPessoa(profile, TIPO_PESSOA.LOJISTA))
  );
}

// Staff interno = ao menos um perfil que não é de acesso externo. Um perfil externo extra
// (ex.: CRC, que é Lojista Multimarca) não tira o acesso dado por um perfil interno (ex.: ADM).
export function userHasInternalAccessProfile(profiles = []) {
  return profiles.some((profile) => !profileMatchesExternalAccess(profile));
}
